import { formatUuidT } from "../../flatbuffers/display";

/**
 * normalizeUserResponse takes a raw `/api/users/me` payload (which may arrive
 * as either plain JSON snake_case fields OR FlatBuffer-decoded objects with
 * `UuidT {value: U128T}` UUIDs) and returns a flat shape that:
 *
 *   - Has every UUID field as a hyphenated STRING (never a UuidT object).
 *   - Carries both snake_case and camelCase aliases for the fields the UI
 *     reads (different components access different cases).
 *   - Has `role` and `org` nested objects with their `id` also flattened to
 *     a string, so `userLogged.role.id` / `userLogged.org.id` are safe to
 *     render directly.
 *
 * This MUST be the single normalization point for any code path that writes
 * to `state.home.userLogged`. Spreading raw API data into Redux without going
 * through here re-introduces the "Objects are not valid as a React child
 * (found: object with keys {value})" error in DrawerUserInformation.
 */
function normalizeUserResponse(raw: any): any {
  if (!raw || typeof raw !== "object") return raw;

  const roleIdStr = formatUuidT(raw.roleId ?? raw.role_id);
  const orgIdStr = formatUuidT(
    raw.organizationId ?? raw.organization_id ?? raw.orgId ?? raw.org_id,
  );
  const roleName = raw.roleName ?? raw.role?.name ?? "";

  const role = raw.role
    ? { ...raw.role, id: formatUuidT(raw.role.id) }
    : { id: roleIdStr, name: roleName, menu: raw.roleMenu };

  const org = raw.org
    ? { ...raw.org, id: formatUuidT(raw.org.id) }
    : {
        id: orgIdStr,
        name: raw.orgName ?? "",
        type: raw.orgType ?? "",
      };

  return {
    ...raw,
    // Snake-case overrides — these clobber any UuidT shapes that came in via
    // the spread above so DrawerUserInformation (which reads snake_case) is
    // also safe.
    id: formatUuidT(raw.id),
    cognito_id: formatUuidT(raw.cognitoId ?? raw.cognito_id),
    role_id: roleIdStr,
    org_id: orgIdStr,
    organization_id: orgIdStr,
    pref_grid_id: formatUuidT(raw.prefGridId ?? raw.pref_grid_id),
    // Camel-case aliases used by Layout/YourProfileDrawer/etc.
    acceptedTerms: raw.acceptedTerms ?? raw.accepted_terms ?? "",
    cognitoId: formatUuidT(raw.cognitoId ?? raw.cognito_id),
    createdOfflinePsw: raw.createdOfflinePsw ?? raw.created_offline_psw ?? false,
    firstName: raw.firstName ?? raw.first_name ?? "",
    middleName: raw.middleName ?? raw.middle_name ?? "",
    lastName: raw.lastName ?? raw.last_name ?? "",
    organizationId: orgIdStr,
    prefGridId: formatUuidT(raw.prefGridId ?? raw.pref_grid_id),
    prefTheme: raw.prefTheme ?? raw.pref_theme ?? "",
    roleId: roleIdStr,
    createdAt: raw.createdAt ?? raw.created_at ?? "",
    updatedAt: raw.updatedAt ?? raw.updated_at ?? "",
    deletedAt: raw.deletedAt ?? raw.deleted_at ?? "",
    role,
    org,
  };
}

export { normalizeUserResponse };
export default normalizeUserResponse;
